import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { AuthenticationResult } from '@azure/msal-common';
import { CalAngularService, ICvxClaimsPrincipal } from '@cvx/cal-angular';
import { Observable, BehaviorSubject, map, lastValueFrom } from 'rxjs';
import { AppConfigService } from '../services/app-config.service';
import { Router } from '@angular/router';
import { IDropdownValue } from '../interfaces/model/common/dropdown';
import { IUserRegionRoleDetail } from '../interfaces/model/common/user-region-role';
import { MODEL_SETTING } from '../shared/constants/modelSettings.constant';
import { WORKSAPCE_USER_ROLE } from '@app/shared/constants/workspace-roles.constant';
import { NotificationService } from '@app/_services/notification.service';
import { RegionRoleService } from '../services/region-role.service';


@Injectable({
  providedIn: 'root',
})
export class CalService {

  private readonly router = inject(Router);
  private readonly regionRoleService = inject(RegionRoleService);

  private userSubject = new BehaviorSubject<ICvxClaimsPrincipal>({} as ICvxClaimsPrincipal);
  claimsPrincipal: ICvxClaimsPrincipal | null = null;
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);

  userRegionRole$: BehaviorSubject<IUserRegionRoleDetail> = new BehaviorSubject<IUserRegionRoleDetail>(null!);
  modelRegions$: BehaviorSubject<IDropdownValue[]> = new BehaviorSubject<IDropdownValue[]>([]);

  userRegionRoleDetails: IUserRegionRoleDetail[] = [];

  // Add this signal to CalService
  readonly currentRegionId = signal<string | null>(null);

  // Add computed signal for complete region details
  readonly currentRegionDetails = linkedSignal({
    source: this.currentRegionId,
    computation: () => {
      const regionId = this.currentRegionId();
      if (!regionId) return null;

      return this.getCurrentUserRegionRole();
    }
  });
  // readonly currentRegionDetails = signal<IUserRegionRoleDetail | null>(null);

  constructor(private messageService:NotificationService, private cal: CalAngularService) {
    const regionFromSession = this.getCurrentUserRegionRole();
    if (regionFromSession !== null) {
      this.currentRegionId.set(regionFromSession.regionId);
    }

    this.regionRoleService.region$.subscribe(region => {
      if (region) {
        this.currentRegionId.set(region);
      }
    });
    this.initializeUser();
  }

  private async initializeUser() {
    try {
      // Check if user is already authenticated
      const account = this.cal.getAccount();
      const hasAuthenticatedSession = !!account;

      if (hasAuthenticatedSession) {
        const userInfo = {
          name: account.name || '',
          userName: account.username || account.idTokenClaims?.['email'] || '',
          email: account.idTokenClaims?.['email'] || '',
          roles: account.idTokenClaims?.['roles'] || []
        };
        this.userSubject.next(userInfo as any);
        this.isLoggedInSubject.next(true);
        // Set user region roles if authenticated
        this.getAndSetUserRegionRoles();
      } else {
        // Optionally trigger sign-in if autoSignIn is configured
        if (AppConfigService.settings?.autoSignIn) {
          await this.signIn();
        }
      }
    } catch (error) {
      console.error('Error initializing user:', error);
    }
  }
  async getAuthToken(
    oidcScopes: string[] = AppConfigService.settings.oidcScopes,
    fullAuthResponse: boolean = false)
    : Promise<string | AuthenticationResult | null> {
    return lastValueFrom(this.cal.getAADToken(oidcScopes, fullAuthResponse));
  }

  async updateAccessToken(): Promise<string> {

    const graphScopes = AppConfigService.settings.graphScopes.slice(0);

    graphScopes.unshift(`${AppConfigService.settings.clientId}/user_impersonation`);

    const token: string = await this.getAADToken(graphScopes, false) as string;
    sessionStorage.setItem('AuthToken', token);
    return token;
  }

  async getAADToken(oidcScopes: string[] = AppConfigService.settings.oidcScopes, fullAuthResponse = false): Promise<string | AuthenticationResult | null> {
    return lastValueFrom(this.cal.getAADToken(oidcScopes, fullAuthResponse));
  }

  getAuthorizationToken(): string | null {
    return window.sessionStorage.getItem('AuthToken');
  }

  isTokenExpired(): boolean {
    const token = this.getAuthorizationToken();
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) return true;
      const expiryDate = new Date(exp * 1000);
      return expiryDate < new Date();
    } catch {
      return true;
    }
  }

  async refreshToken(): Promise<string | null> {
    try {
      const token = await this.getAADToken() as string | null;
      if (token) {
        window.sessionStorage.setItem('AuthToken', token);
      }
      return token;
    } catch {
      return null;
    }
  }

  isUserLoggedIn(): Observable<boolean> {
    return this.isLoggedInSubject.asObservable();
  }

  getUserInfo(): Observable<ICvxClaimsPrincipal> {
    return this.userSubject.asObservable();
  }

  public getFirstName() {
    return this.userSubject.pipe(map((user) => {
      // console.log('User object:', user);
      // console.log("user.userName.split('@')[0];",user.userName.split('@')[0]);
      if (user && user?.name && user?.userName) {
        localStorage.setItem("userName", user.userName.split('@')[0]);
        localStorage.setItem("userEmail", user.userName);
        // console.log('User name:', user.name);
        // Parse the name format: "LastName, FirstName [UserName]"
        const nameParts = user.name.split(',');
        if (nameParts.length > 1) {
          const firstNamePart = nameParts[1].split('[')[0].trim();
          return firstNamePart;
        }
      }
      return '';
    }));
  }

  public getNamePart() {
    return this.userSubject.pipe(map((user) => {
      if (user.name && user.userName) {
        return user.userName.split('@')[0];
      }
      return '';
    }));
  }

  public getUserRoles(): string[] {
    try {
      const user = this.cal?.getAccount?.();
      if (user && user.idTokenClaims) {
        const roles = user.idTokenClaims['roles'];
        if (roles && Array.isArray(roles)) {
          return roles;
        }
      }
    } catch (error) {
      console.warn('Error retrieving user roles (account may not be ready):', error);
    }
    return [];
  }

  public isUserAdmin(): boolean {
    // const sessionRole = sessionStorage.getItem("sessionRegionRole");
    const selectedRole = localStorage.getItem("selectedRegion");
    const selectedRegion = localStorage.getItem("selectedRole");
    const sessionRole = '{"regionId":"USSaltLake","regionValue":"US Saltlake","role":"Administrator"}';

    let userSessionRole: IUserRegionRoleDetail;
    if (!(selectedRole && selectedRegion)) {
      this.router.navigate([""]);
      this.messageService.showError("Please select region and role to proceed.");
      return false;
    }


    const hasAdminRole = selectedRole == WORKSAPCE_USER_ROLE.ADMIN;

    if (sessionRole !== null && hasAdminRole) {
      return true;
    }
    else {
      return false;
    }
  }

  /**
   * @description Determines whether user admin or vcoplanner is
   * @returns true if user admin or vcoplanner 
   */
  public isUserAdminOrVCOPlanner(): boolean {
    const sessionRole = sessionStorage.getItem("sessionRegionRole");
    const selectedRole = localStorage.getItem("selectedRegion");
    const selectedRegion = localStorage.getItem("selectedRole");
    let userSessionRole: IUserRegionRoleDetail;
    if (!(selectedRole && selectedRegion)) {
      this.router.navigate([""]);
      this.messageService.showError("Please select region and role to proceed.");
      return false;
    }


    const hasAdminOrVCOPlannerRole = selectedRole == WORKSAPCE_USER_ROLE.ADMIN || selectedRegion == WORKSAPCE_USER_ROLE.VCO_PLANNER;

    if (sessionRole !== null && hasAdminOrVCOPlannerRole) {
      return true;
    }
    else {
      return false;
    }
  }

  // need to remove this method going forward
  public getAndSetUserRegionRoles() {
    const regionRoles = this.getUserRoles();

    if (regionRoles.length === 0) {
      // do not allow that user
      this.router.navigate([""]);
      return;
    }

    if (this.getUserRegionRoleDetails().length !== 0) {
      return; // already set
    }

    // seprate region and roles
    const uniqueRegions: Set<string> = new Set<string>();

    regionRoles.forEach(regionRole => {
      const regionRoleArr = regionRole.split('.');
      const regionObj = MODEL_SETTING.MODEL_REGION.find(modelRegion => modelRegion.id == regionRoleArr[0]);
      // Exclude regions with value "Thailand" or "Singapore"
      if (regionObj && regionObj.value !== "Thailand" && regionObj.value !== "Singapore") {
        this.userRegionRoleDetails.push(
          {
            regionId: regionRoleArr[0],
            regionValue: regionObj.value!,
            role: regionRoleArr[1]
          });
        uniqueRegions.add(regionRoleArr[0]);
      }
    });

    // make dropdown for region
    const userRegions: IDropdownValue[] = [];

    uniqueRegions.forEach(region => {
      const res = MODEL_SETTING.MODEL_REGION.find(a => a.id == region)!;
      if (res !== undefined) {
        userRegions.push({ id: res.id, value: res.value });
      }
    });

    this.modelRegions$.next(userRegions);

    sessionStorage.setItem("userRegionRoleDetails", JSON.stringify(this.userRegionRoleDetails));
  }

  public getUserRegionRoleDetails(): IUserRegionRoleDetail[] {
    let userRegionRoleDeatils: IUserRegionRoleDetail[] = [];
    const fromSession = sessionStorage.getItem("userRegionRoleDetails");

    if (fromSession != null) {
      userRegionRoleDeatils = JSON.parse(fromSession) as IUserRegionRoleDetail[];
    }

    return userRegionRoleDeatils;
  }

  public getCurrentUserRegionRole(): IUserRegionRoleDetail | null {
    let userRegionRoleDeatils: IUserRegionRoleDetail | null = null;
    const fromSession = sessionStorage.getItem("sessionRegionRole");
    const fromUserChanged = this.userRegionRole$.value;
    if (fromSession === null) {
      userRegionRoleDeatils = null;
    }
    else {
      userRegionRoleDeatils = JSON.parse(fromSession) as IUserRegionRoleDetail;

      if (fromUserChanged !== null && userRegionRoleDeatils !== fromUserChanged) {
        // update with latest
        userRegionRoleDeatils = fromUserChanged;
        sessionStorage.setItem("sessionRegionRole", JSON.stringify(fromUserChanged));
      }
    }
    return userRegionRoleDeatils;
  }

  public getRoleRegion() {
    let userRegionRoleDeatils = localStorage.getItem('selectedRegion');
    return userRegionRoleDeatils
  }
  public getSelectedRole() {
    let userRegionRoleDeatils = localStorage.getItem('selectedRole');
    return userRegionRoleDeatils
  }

  public setCurrentUserRegionRole(userRegionRole: IUserRegionRoleDetail) {
    const regionValue = MODEL_SETTING.MODEL_REGION.find(modelRegion => modelRegion.id == userRegionRole.regionId)!.value;
    userRegionRole.regionValue = regionValue;
    this.userRegionRole$.next(userRegionRole);
    sessionStorage.setItem("sessionRegionRole", JSON.stringify(userRegionRole));
    // Update the signal here
    this.currentRegionId.set(userRegionRole.regionId);

    // this.messageService.showInfo("Region Selected", `${regionValue} region with ${userRegionRole.role} is being selected.`);
    // this.router.navigate(["model/locations"]);
    // window.location.href = "/"; // navigate to home page
  }

  public async signIn(): Promise<boolean> {
    return lastValueFrom(this.cal.userInitiatedSignIn()).then(async data => {
      const account = this.cal.getAccount?.();
      const hasAccount = !!account;

      if (data) {
        this.userSubject.next(data);
      } else if (account) {
        const userInfo = {
          name: account.name || '',
          userName: account.username || account.idTokenClaims?.['email'] || '',
          email: account.idTokenClaims?.['email'] || '',
          roles: account.idTokenClaims?.['roles'] || []
        };
        this.userSubject.next(userInfo as any);
      }

      this.isLoggedInSubject.next(data != null || hasAccount);
      // update the token to be able to reach out to the backend
      await this.updateAccessToken();

      if (!data && !hasAccount) {
        console.error('User failed to log in.');
      }

      return data != null || hasAccount;
    });
  }
}
